
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

/**
 * Updates user validation status
 * @param phoneNumber The user's phone number
 * @returns Whether the update was successful
 */
export const updateUserValidationStatus = async (phoneNumber: string): Promise<boolean> => {
  try {
    const { data: userData, error: userError } = await supabase
      .from('users')
      .select('user_id')
      .eq('phone_number', phoneNumber)
      .single();
    
    if (userError) throw userError;
    
    const { error } = await supabase
      .from('users')
      .update({
        validated: true
      })
      .eq('user_id', userData.user_id);
      
    if (error) throw error;
    
    return true;
  } catch (error) {
    console.error("Error updating user validation status:", error);
    return false;
  }
};

/**
 * Gets a user by phone number
 * @param phoneNumber The phone number to look up
 * @returns User data or null if not found
 */
export const getUserByPhoneNumber = async (phoneNumber: string): Promise<{
  user_id: string;
  full_name: string;
  validated: boolean;
} | null> => {
  try {
    const { data, error } = await supabase
      .from('users')
      .select('user_id, full_name, validated')
      .eq('phone_number', phoneNumber)
      .maybeSingle();
    
    if (error) throw error;
    return data;
  } catch (error) {
    console.error("Error getting user by phone number:", error);
    return null;
  }
};

/**
 * Get a user's phone number based on user_id
 * @param userId The user ID to look up
 * @returns Phone number or null if not found
 */
export const getUserPhoneNumber = async (userId: string): Promise<string | null> => {
  try {
    const { data, error } = await supabase
      .from('users')
      .select('phone_number')
      .eq('user_id', userId)
      .single();
    
    if (error) throw error;
    return data.phone_number;
  } catch (error) {
    console.error("Error getting user phone number:", error);
    return null;
  }
};

export const userService = {
  updateUserValidationStatus,
  getUserByPhoneNumber,
  getUserPhoneNumber
};
